import type { Lender, Outcome, FieldDef } from "../types";
import { Icon } from "./Icon";
import { FieldRow } from "./FieldRow";
import { OutcomeLogger } from "./OutcomeLogger";

interface Props {
  lender: Lender;
  defs: Record<string, FieldDef>;
  copiedKey: string | null;
  onCopy: (key: string, value: string, label: string) => void;
  ssnRevealed: boolean;
  onToggleSsn: () => void;
  outcomes: Outcome[];
  onLogOutcome: (o: Omit<Outcome, "at">) => Promise<unknown> | void;
}

export function LenderPanel({
  lender,
  defs,
  copiedKey,
  onCopy,
  ssnRevealed,
  onToggleSsn,
  outcomes,
  onLogOutcome,
}: Props) {
  return (
    <div className="lender">
      <header className="lender__head">
        <div className="lender__title-wrap">
          <span className="lender__swatch" style={{ background: lender.swatch }} />
          <div>
            <h2 className="lender__title">{lender.name}</h2>
            <p className="lender__tag">{lender.tag}</p>
          </div>
        </div>
        <a className="lender__portal" href={lender.portal} target="_blank" rel="noopener noreferrer">
          Open portal <Icon.External width={13} height={13} />
        </a>
      </header>

      <p className="lender__blurb">{lender.blurb}</p>

      <div className="lender__section">
        <div className="lender__section-head">
          <h3>Required fields</h3>
          <span className="lender__count">
            {lender.fieldKeys.length} fields · click any to copy
          </span>
        </div>
        <div className="lender__fields">
          {lender.fieldKeys.map((k) => {
            const def = defs[k];
            if (!def) return null;
            const namespacedKey = `${lender.id}:${k}`;
            return (
              <FieldRow
                key={k}
                fieldKey={namespacedKey}
                def={def}
                copiedKey={copiedKey}
                onCopy={(_, value, label) => onCopy(namespacedKey, value, label)}
                ssnRevealed={ssnRevealed}
                onToggleSsn={onToggleSsn}
              />
            );
          })}
        </div>
      </div>

      <div className="lender__tip">
        <span className="lender__tip-label">Tip</span>
        <p>{lender.tip}</p>
      </div>

      <OutcomeLogger lender={lender} outcomes={outcomes} onLog={onLogOutcome} />
    </div>
  );
}
